import { useEffect, useState } from "react";
import { useLang } from "@/i18n";
import api, { errMsg } from "@/api";
import SiteNav from "@/components/SiteNav";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Users, CalendarClock, Wallet, MailOpen, BookOpen, ClipboardList, FolderOpen } from "lucide-react";
import ConsultsTab from "@/components/admin/ConsultsTab";
import BookingsTab from "@/components/admin/BookingsTab";
import StudentsTab from "@/components/admin/StudentsTab";
import MaterialsTab from "@/components/admin/MaterialsTab";
import QuizzesTab from "@/components/admin/QuizzesTab";

export default function AdminDashboard() {
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overview");

  const loadStats = () => {
    api.get("/admin/overview").then((r) => setStats(r.data)).catch((e) => toast.error(errMsg(e)));
  };

  useEffect(() => { loadStats(); }, []);

  const handleTab = (v) => { setTab(v); if (v === "overview") loadStats(); };

  const cards = [
    { icon: Users, value: stats?.students, label: t("admin.totalStudents"), tid: "stat-students" },
    { icon: CalendarClock, value: stats?.pending_bookings, label: t("admin.pendingBookings"), tid: "stat-pending-bookings" },
    { icon: Wallet, value: stats?.awaiting_payment, label: t("admin.awaitingPayment"), tid: "stat-awaiting-payment" },
    { icon: MailOpen, value: stats?.new_consults, label: t("admin.newConsults"), tid: "stat-new-consults" },
    { icon: BookOpen, value: stats?.lessons_scheduled, label: t("admin.scheduledLessons"), tid: "stat-scheduled-lessons" },
    { icon: ClipboardList, value: stats?.quizzes, label: t("admin.quizzes"), tid: "stat-quizzes" },
    { icon: FolderOpen, value: stats?.materials, label: t("admin.materials"), tid: "stat-materials" },
  ];

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <SiteNav />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-2xl sm:text-3xl font-serif font-bold" data-testid="admin-title">{t("nav.admin")}</h1>

        <Tabs value={tab} onValueChange={handleTab} className="mt-6">
          <TabsList className="flex-wrap h-auto" data-testid="admin-tabs">
            <TabsTrigger value="overview" data-testid="tab-admin-overview">{t("admin.overview")}</TabsTrigger>
            <TabsTrigger value="consults" data-testid="tab-admin-consults">{t("admin.consults")}</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-admin-bookings">{t("admin.bookings")}</TabsTrigger>
            <TabsTrigger value="students" data-testid="tab-admin-students">{t("admin.students")}</TabsTrigger>
            <TabsTrigger value="materials" data-testid="tab-admin-materials">{t("admin.materials")}</TabsTrigger>
            <TabsTrigger value="quizzes" data-testid="tab-admin-quizzes">{t("admin.quizzes")}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {cards.map(({ icon: Icon, value, label, tid }) => (
                <Card key={tid} data-testid={tid}>
                  <CardContent className="p-5 flex items-center gap-4">
                    <span className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5" />
                    </span>
                    <div>
                      <p className="text-2xl font-serif font-bold">{value ?? "…"}</p>
                      <p className="text-xs text-muted-foreground">{label}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="consults" className="mt-6"><ConsultsTab onChanged={loadStats} /></TabsContent>
          <TabsContent value="bookings" className="mt-6"><BookingsTab onChanged={loadStats} /></TabsContent>
          <TabsContent value="students" className="mt-6"><StudentsTab /></TabsContent>
          <TabsContent value="materials" className="mt-6"><MaterialsTab /></TabsContent>
          <TabsContent value="quizzes" className="mt-6"><QuizzesTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
